import React, { useContext } from "react";
import { NavLink } from "react-router-dom";
import { LogOut, Moon, Sun } from "lucide-react";

import { Sheet, SheetContent } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import { AuthContext } from "../context/Auth/AuthContext";
import { useThemeContext } from "../context/DarkMode";
import GlobalSearch from "../components/GlobalSearch";
import NotificationCenter from "../components/NotificationCenter";
import { useSecoesDoMenu } from "./MainListItems";

// O menu completo do celular, em grade de ícones.
//
// A gaveta lateral é padrão de desktop: uma lista vertical de 38 linhas que,
// numa tela de 390px, obriga a rolar muito para achar qualquer coisa e cobre
// o conteúdo por um lado só. Em aplicativo de celular o "Mais" abre uma
// bandeja que sobe, com tudo à vista em ícones grandes — quatro por linha,
// alvo confortável para o dedo, e o rótulo embaixo para não virar adivinhação.
//
// A lista de seções NÃO mora mais aqui: vem de useSecoesDoMenu, a mesma fonte
// que o menu lateral e o menu do topo usam. Antes esta tela tinha a própria
// cópia, escrita à mão, e a cópia ficou para trás — as Mensagens agendadas
// viraram outro nome de um lado e não do outro, e quatorze telas (Tarefas,
// Prospecção, Redes sociais inteiras, Modelos WhatsApp, Logs de Auditoria,
// Inteligência) existiam no desktop e não tinham porta de entrada nenhuma no
// celular. Com fonte única, tela nova nasce nos três lugares.
//
// As regras continuam as mesmas do menu lateral: quem não pode administrar
// não vê Configurações, o que está fora do plano aparece apagado (some era
// pior — quem não vê o recurso não sabe que ele existe e não tem o que pedir)
// e a Gestão SaaS só existe para o admin da empresa operadora.

const Item = ({
	to,
	icone: Icone,
	rotulo,
	foraDoPlano,
	exato,
	aviso,
	aoNavegar,
}) => (
	<NavLink
		to={to}
		// `exato` vem junto com a seção: "/zernio" é prefixo das outras seis,
		// e sem isso o item "Contas" fica aceso dentro de "Publicações".
		end={exato}
		onClick={aoNavegar}
		title={
			foraDoPlano
				? `${rotulo} — não está no seu plano. Fale com quem administra a conta.`
				: undefined
		}
		className={({ isActive }) =>
			cn(
				// Altura fixa, igual para todos. Com altura automática, o único
				// rótulo que quebra em duas linhas ("Config. campanhas") deixava
				// a célula 14px mais alta e empurrava a linha inteira para
				// baixo, desalinhando a grade. Aqui todo item reserva as duas
				// linhas, use ou não.
				"flex h-[88px] flex-col items-center justify-start gap-1.5 rounded-xl p-1.5 text-center transition-colors",
				"active:bg-accent",
				isActive ? "bg-primary/10" : "hover:bg-accent",
				foraDoPlano && "opacity-40"
			)
		}
	>
		{({ isActive }) => (
			<>
				<span
					className={cn(
						"relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
						isActive
							? "bg-primary text-primary-foreground"
							: "bg-muted text-muted-foreground"
					)}
				>
					<Icone className="h-5 w-5" />
					{aviso && (
						<span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-destructive ring-2 ring-card" />
					)}
				</span>
				{/* Duas linhas no máximo: numa célula de 95px, um rótulo longo
				    sem limite empurraria a altura de novo. */}
				<span
					className={cn(
						"line-clamp-2 text-[11px] leading-tight",
						isActive ? "font-semibold text-primary" : "text-foreground"
					)}
				>
					{rotulo}
				</span>
			</>
		)}
	</NavLink>
);

// Seção sem título é item solto, fora de grupo: o Dashboard, que é a tela
// inicial e não pertence a nenhum conjunto.
const Secao = ({ titulo, children }) => (
	<div>
		{titulo && (
			<p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
				{titulo}
			</p>
		)}
		<div className="grid grid-cols-4 gap-1">{children}</div>
	</div>
);

const MenuDeIcones = ({ aberto, aoFechar, aoAbrirPerfil, aoSair }) => {
	const { user } = useContext(AuthContext);
	const { darkMode: temaEscuro, toggleTheme: alternarTema } =
		useThemeContext();
	const secoes = useSecoesDoMenu();

	const fechar = () => aoFechar();

	return (
		<Sheet open={aberto} onOpenChange={(v) => !v && aoFechar()}>
			<SheetContent
				side="bottom"
				// Quase a tela toda: menu que mostra metade obriga a rolar antes
				// de saber o que existe. A safe area evita que a última linha
				// caia atrás do indicador de início do iPhone.
				className="h-[88dvh] rounded-t-2xl p-0 md:hidden"
			>
				<div className="flex h-full flex-col">
					{/* Quem está usando e as ações da conta.
					    A barra de cima saiu do celular — ela roubava 56px de
					    altura em toda tela para mostrar coisas que se usam de vez
					    em quando. O que estava lá mora aqui: perfil, tema e sair. */}
					<div className="flex shrink-0 items-center gap-3 border-b px-4 pb-3 pt-4">
						<span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold uppercase text-muted-foreground">
							{(user?.name || "?").slice(0, 2)}
						</span>
						<button
							type="button"
							className="min-w-0 flex-1 rounded-lg text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
							onClick={() => {
								aoAbrirPerfil();
								fechar();
							}}
						>
							<p className="truncate text-sm font-semibold">{user?.name}</p>
							<p className="truncate text-xs text-muted-foreground">
								{user?.email}
							</p>
						</button>

						{/* Busca e avisos também vinham da barra de cima. Ficam na
						    linha da conta, sempre a um toque do "Mais". */}
						{user?.id && <GlobalSearch />}
						{user?.id && <NotificationCenter />}

						<Button
							variant="ghost"
							size="icon"
							className="h-10 w-10 shrink-0"
							aria-label={temaEscuro ? "Tema claro" : "Tema escuro"}
							onClick={alternarTema}
						>
							{temaEscuro ? (
								<Sun className="h-5 w-5" />
							) : (
								<Moon className="h-5 w-5" />
							)}
						</Button>
						<Button
							variant="ghost"
							size="icon"
							className="h-10 w-10 shrink-0 text-destructive hover:text-destructive"
							aria-label="Sair"
							onClick={aoSair}
						>
							<LogOut className="h-5 w-5" />
						</Button>
					</div>

					<div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-3 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
						{secoes.map((secao) => (
							<Secao key={secao.id} titulo={secao.label}>
								{secao.itens.map((item) => (
									<Item
										key={item.to}
										to={item.to}
										icone={item.icon}
										rotulo={item.label}
										foraDoPlano={item.foraDoPlano}
										exato={item.exato}
										aviso={item.badge}
										aoNavegar={fechar}
									/>
								))}
							</Secao>
						))}
					</div>
				</div>
			</SheetContent>
		</Sheet>
	);
};

export default MenuDeIcones;
